import AFRICA from '@/store/state/data/Africa.geo.json';
import ASIA from '@/store/state/data/Asia.geo.json';
import CA from '@/store/state/data/CA.geo.json';
import CARIBBEAN from '@/store/state/data/caribbean.geo.json';
import EU from '@/store/state/data/EU.geo.json';
import NA from '@/store/state/data/NA.geo.json';
import OCEANIA from '@/store/state/data/Oceania.geo.json';
import SA from '@/store/state/data/SA.geo.json';
import ME from '@/store/state/data/ME.geo.json';
import COUNTRIES from '@/store/state/data/countries.geo.json';

type Resource = GeoJSON.FeatureCollection<GeoJSON.MultiPolygon, {name: string; countries: string[]}>;

import {ref, computed} from '@vue/composition-api';
import {toggleSolutionTags, getAllSolutionFilters, getSelectedGeo} from '@/store/state/helpers';
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import {debounce} from 'debounce';
import {UserActivityCategoryEnum} from '@mobi-components/global-solution';

export function useMap(emit: (type: string, data?: unknown) => void) {
  const regions = ref<Resource[]>([AFRICA, ASIA, CA, CARIBBEAN, EU, NA, OCEANIA, SA, ME]);
  const countries = ref<Resource[]>([COUNTRIES]);
  const visibleBounds = ref(new mapboxgl.LngLatBounds([0, 0], [0, 0]));

  const zoomLevel = ref(0);
  const COUNTRY_LEVEL = 2.5;
  const resources = ref<Resource[]>([]);

  const updateResources = debounce(() => {
    if (zoomLevel.value < COUNTRY_LEVEL) resources.value = regions.value;
    if (zoomLevel.value >= COUNTRY_LEVEL) {
      resources.value = countries.value.filter((x) => {
        return x.features.some((feature) => {
          const depth = feature.geometry.type === 'MultiPolygon' ? 2 : 1;
          const coordinates = feature.geometry.coordinates.flat(depth) as [number, number][];
          return coordinates.some((point) => {
            return visibleBounds.value.contains(point);
          });
        });
      });
    }
  }, 300);

  const getShownMarkers = computed<string[]>(() => {
    if (zoomLevel.value >= COUNTRY_LEVEL) {
      return [];
    }
    return regions.value.map((x) => x.features.map((y) => y.id?.toString() || '')).flat();
  });

  const onSelectRegion = debounce(
    (features: GeoJSON.Feature<GeoJSON.MultiPolygon, {name: string; countries: string[]}>[]) => {
      const first = features.find((x) => x.properties.name);
      const tags = getAllSolutionFilters();
      const selectionChange = tags.find(
        (x) => x.name === first?.properties.name && x.domain === UserActivityCategoryEnum.GEOLOCATION,
      );
      if (selectionChange) {
        toggleSolutionTags([
          {
            ...selectionChange,
            value: !selectionChange.value,
          },
        ]);
      }
    },
    100,
  );

  const getSelection = computed(() => {
    const selectedGeo = getSelectedGeo() || [];
    const selected = resources.value
      .map((x) => x.features)
      .flat()
      .filter((x) => selectedGeo.indexOf(x.properties.name) >= 0)
      .map((x) => x.id);
    return selected;
  });

  const bounds = computed(() => {
    // const localBounds = resources.value.reduce((b: mapboxgl.LngLatBounds | null, geo) => {
    //   return geo.features.reduce((fb: mapboxgl.LngLatBounds | null, feature) => {
    //     const depth = feature.geometry.type === 'MultiPolygon' ? 2 : 1;
    //     const coordinates = feature.geometry.coordinates.flat(depth) as [number, number][];
    //     coordinates.forEach((x) => {
    //       if (fb === null) {
    //         fb = new mapboxgl.LngLatBounds(x, x);
    //       }
    //       fb.extend(x);
    //     });
    //     return fb;
    //   }, b);
    // }, null);
    // return localBounds || new mapboxgl.LngLatBounds([0, 0], [0, 0]);
    return new mapboxgl.LngLatBounds([-50, 80], [50, -60]);
  });

  const seeResults = () => {
    emit('seeResults');
  };

  const onZoomChanged = (zoom: number) => {
    zoomLevel.value = zoom;
    updateResources();
  };

  const onBoundsUpdated = (bounds: mapboxgl.LngLatBounds) => {
    visibleBounds.value = bounds;
    updateResources();
  };

  const onMapLoad = () => {
    updateResources();
  };

  const onSubmitFilters = () => {
    emit('close');
  };

  return {
    getSelection,
    onSelectRegion,
    resources,
    getShownMarkers,
    bounds,
    seeResults,
    onZoomChanged,
    onBoundsUpdated,
    onMapLoad,
    onSubmitFilters,
  };
}
